import { Amenity, IAmenity } from "./amenity.model";
import { AppUser, IAppUser } from "./app-user.model";
import { FileMedia, IFileMedia } from "./file-media.model";
import { HouseFlat, IHouseFlat } from "./house-flat.model";
import { IStreetBuilding, StreetBuilding } from "./street-building.model";

export interface IReport {
  reportId: string;
  neighborhoodId: string;
  streetBuildingId?: string;
  houseFlatId?: string;
  amenityId?: string;
  title: string;
  description: string;
  houseFlatIdReportedBy?: string;
  /* Calle y casa DEL RESIDENTE que levantó el reporte, no del lugar del
     problema. Vienen planas desde la API y llegan null cuando el reporte lo
     creó un admin desde la consola. */
  reportedByStreetBuildingName?: string | null;
  reportedByHouseFlatNumber?: string | null;
  status: number;
  /* Cancelación. Cancelar NUNCA borra: el reporte conserva su status y solo
     cambia `active` a false. Los tres campos llegan del backend nuevo; mientras
     no esté desplegado no vienen y el modelo los da por "reporte vivo". */
  active?: boolean | null;
  uidCanceledBy?: string | null;
  cancelReason?: string | null;
  canceledBy?: IAppUser | null;
  uidCreatedBy?: string;
  uidUpdatedBy?: string;
  createdAt: string;
  updatedAt: string;
  files: IFileMedia[];
  streetBuilding?: IStreetBuilding;
  houseFlat?: IHouseFlat;
  amenity?: IAmenity;
  createdBy?: IAppUser;
}

export class Report {

  reportId: string;
  neighborhoodId: string;
  streetBuildingId?: string;
  houseFlatId?: string;
  amenityId?: string;
  title: string;
  description: string;
  houseFlatIdReportedBy?: string;
  reportedByStreetBuildingName?: string;
  reportedByHouseFlatNumber?: string;
  status: number;
  active: boolean;
  uidCanceledBy?: string;
  cancelReason: string;
  canceledBy?: AppUser;
  uidCreatedBy?: string ;
  uidUpdatedBy?: string;
  createdAt: string;
  updatedAt: string;
  files: FileMedia[];
  streetBuilding?: StreetBuilding;
  houseFlat?: HouseFlat;
  amenity?: Amenity;
  createdBy?: AppUser;

  constructor(
    data: IReport
  ) {
    this.reportId = data.reportId;
    this.neighborhoodId = data.neighborhoodId;
    this.streetBuildingId = data.streetBuildingId;
    this.houseFlatId = data.houseFlatId;
    this.amenityId = data.amenityId;
    this.title = data.title;
    this.description = data.description;
    this.houseFlatIdReportedBy = data.houseFlatIdReportedBy;
    /* La API manda null cuando reportó un admin, y mientras el backend nuevo
       no esté desplegado el campo ni siquiera viene. Ambos casos se guardan
       como undefined para que el resto del modelo tenga un solo caso vacío. */
    this.reportedByStreetBuildingName = data.reportedByStreetBuildingName ?? undefined;
    this.reportedByHouseFlatNumber = data.reportedByHouseFlatNumber ?? undefined;
    this.status = Number(data.status);
    /* Mismo criterio que los campos de arriba: lo que la API no manda no es un
       dato en falso, es un dato que todavía no existe. Un reporte sin `active`
       es un reporte vivo, así que solo el false explícito lo cancela y la
       pantalla nunca pinta "undefined" ni oculta acciones por un campo
       ausente. */
    this.active = data.active === null || data.active === undefined
      ? true
      : data.active === true;
    this.uidCanceledBy = data.uidCanceledBy ?? undefined;
    /* La razón vive como cadena: '' cuando el reporte no está cancelado. */
    this.cancelReason = data.cancelReason ?? '';
    this.canceledBy = data.canceledBy
      ? new AppUser(data.canceledBy)
      : undefined;
    this.uidCreatedBy = data.uidCreatedBy;
    this.uidUpdatedBy = data.uidUpdatedBy;
    this.createdAt = data.createdAt;
    this.updatedAt = data.updatedAt;
    this.files = data.files
      ? data.files.map(
          e => new FileMedia(e)
        )
      : [];
    this.streetBuilding = data.streetBuilding
      ? new StreetBuilding(data.streetBuilding)
      : undefined;
    this.houseFlat = data.houseFlat
      ? new HouseFlat(data.houseFlat)
      : undefined;
    this.amenity = data.amenity
      ? new Amenity(data.amenity)
      : undefined;
    this.createdBy = data.createdBy
      ? new AppUser(data.createdBy)
      : undefined;
  }

  /* Un residente levantó el reporte desde la app: el backend guarda su casa en
     houseFlatIdReportedBy. Si lo creó un admin desde la consola el campo va
     vacío. */
  get isReportedByResident(): boolean {
    return !!this.houseFlatIdReportedBy;
  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR CANCELACIÓN                             */
  /* -------------------------------------------------------------------------- */

  /* Cancelado = active en false. El status NO cambia al cancelar, así que
     preguntar por el status para saber si un reporte sigue vivo da siempre la
     respuesta equivocada. */
  get isCanceled(): boolean {
    return !this.active;
  }

  /* Quién canceló, ya resuelto. Guion cuando la API no trae la cuenta. */
  get canceledByLabel(): string {

    const fullName = this.canceledBy?.fullName?.trim();

    return fullName
      ? fullName
      : '—';

  }

  /**
   * Matriz de permisos de la consola, en un solo lugar.
   *
   * Vive aquí y no en las plantillas porque el contexto de la tabla de reportes
   * es `let-report` —o sea `any`—: una condición mal escrita en ese HTML no la
   * detecta el compilador. Desde el modelo sí.
   *
   * | Creado por | Estatus         | Admin (consola)  |
   * | Residente  | Pendiente (-1)  | Cancelar + razón |
   * | Residente  | En progreso (0) | Cancelar + razón |
   * | Residente  | Resuelto (1)    | nada             |
   * | Admin      | Pendiente (-1)  | BORRAR (físico)  |
   * | Admin      | En progreso (0) | Cancelar + razón |
   * | Admin      | Resuelto (1)    | nada             |
   *
   * Un reporte ya cancelado no admite ninguna acción más, y un resuelto es
   * intocable para todos.
   */

  /* Borrado físico: SOLO el reporte que levantó un admin y que nadie ha
     empezado a atender. Todo lo demás se cancela, nunca se borra. */
  get canBeDeleted(): boolean {
    return this.active
      && !this.isReportedByResident
      && this.status === -1;
  }

  /* Cancelación con razón obligatoria. El caso admin + propio + pendiente cae
     fuera a propósito: ese reporte se BORRA, y el botón de cancelar ni siquiera
     se pinta (el 400 del backend es red de seguridad, no flujo de UI). */
  get canBeCanceled(): boolean {

    if (!this.active) {
      return false;
    }

    if (this.status >= 1) {
      return false;
    }

    return !this.canBeDeleted;

  }

  /* Avanzar de estado. Un cancelado ya está cerrado: no se mueve. */
  get canAdvanceStatus(): boolean {
    return this.active && this.status < 1;
  }

  /* Domicilio de quien reportó, ya resuelto: "Calle · Casa 3". Devuelve cadena
     vacía si no hay nada que mostrar —reportó un admin, o el backend todavía
     no manda los campos—, para que la plantilla solo tenga que preguntar por
     la verdad de la etiqueta y nunca pinte "undefined". */
  get reportedByAddressLabel(): string {

    const parts: string[] = [];

    const streetBuildingName = this.reportedByStreetBuildingName?.trim();
    const houseFlatNumber = this.reportedByHouseFlatNumber?.trim();

    if (streetBuildingName) {
      parts.push(streetBuildingName);
    }

    if (houseFlatNumber) {
      parts.push(`Casa ${houseFlatNumber}`);
    }

    return parts.join(' · ');

  }

  get nextStatusString(): string {

    let status = '';

    switch (this.status) {
      case -1:
        status = 'En Proceso';
        break;
      case 0:
        status = 'Completado';
        break;
      default:
        status = 'Desconocido';
        break;
    }

    return status;

  }

  get nextStatus(): number {

    let status = -1;

    switch (this.status) {
      case -1:
        status = 0;
        break;
      case 0:
        status = 1;
        break;
      default:
        status = 1;
        break;
    }

    return status;

  }

  get statusLabel(): string {

    let status = '';

    /* La cancelación se pregunta ANTES del switch: un reporte cancelado
       conserva su status en la base, así que sin esto un cancelado seguiría
       diciendo "Pendiente". */
    if (this.isCanceled) {
      return 'Cancelado';
    }

    switch (this.status) {
      case -1:
        status = 'Pendiente';
        break;
      case 0:
        status = 'En Proceso';
        break;
      default:
        status = 'Completado';
        break;
    }

    return status;

  }

}
